import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../lib/firebase';
import toast from 'react-hot-toast';
import {
  UserPlusIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon,
  SunIcon,
  MoonIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import Logo from '../components/icons/Logo';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

interface SignupInfo {
  customer: { id: string; name: string; email: string; type: string; companyName: string };
  contactName: string | null;
  organizationName: string;
}
const signupApi = {
  get: httpsCallable<{ token: string }, SignupInfo>(functions, 'customerSignupGet'),
  complete: httpsCallable<{ token: string; password: string }, { ok: boolean; email: string }>(functions, 'customerSignupComplete')
};

const CustomerSignup: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { language, setLanguage } = useLanguage();
  
  const [info, setInfo] = useState<SignupInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    if (!token) { setError('Érvénytelen regisztrációs link'); setLoading(false); return; }
    try {
      const res = await signupApi.get({ token });
      setInfo(res.data);
    } catch (err: any) {
      console.error('Error validating token:', err);
      setError(err?.message?.replace(/^.*?:\s*/, '') || 'Érvénytelen vagy lejárt regisztrációs link');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!info || !token) return;
    if (password.length < 6) { toast.error('A jelszónak legalább 6 karakter hosszúnak kell lennie'); return; }
    if (password !== confirmPassword) { toast.error('A jelszavak nem egyeznek'); return; }
    setIsSubmitting(true);
    try {
      const res = await signupApi.complete({ token, password });
      // A fiókot a szerver hozta létre – itt már csak bejelentkezünk vele
      await signInWithEmailAndPassword(auth, res.data.email, password);
      setSuccess(true);
      toast.success('Sikeres regisztráció! Átirányítás...');
      setTimeout(() => navigate('/customer-portal'), 1500);
    } catch (err: any) {
      console.error('Error creating user:', err);
      toast.error(err?.message?.replace(/^.*?:\s*/, '') || 'Hiba történt a regisztráció során');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen relative bg-[#f7f7f9] dark:bg-[#0b0d12] flex items-center justify-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[#f7f7f9] dark:bg-[#0b0d12]">
            <div className="hidden" />
          </div>
        </div>
        <div className="relative animate-spin rounded-full h-12 w-12 border-b-2 border-[#0f7f75]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7f7f9] dark:bg-[#0b0d12] p-4">
        <div className="max-w-md w-full bg-white dark:bg-[#12151c] rounded-2xl border border-gray-200 dark:border-[#2a2f3c] shadow-sm p-8">
          <div className="text-center">
            <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-500" />
            <h2 className="mt-4 text-xl font-bold text-gray-900 dark:text-gray-100">
              Hiba
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {error}
            </p>
            <button
              onClick={() => navigate('/login')}
              className="mt-6 h-10 px-5 inline-flex items-center rounded-lg bg-[#0f7f75] text-white text-sm font-medium hover:bg-[#0c6b62]"
            >
              Bejelentkezés
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7f7f9] dark:bg-[#0b0d12] p-4">
        <div className="max-w-md w-full bg-white dark:bg-[#12151c] rounded-2xl border border-gray-200 dark:border-[#2a2f3c] shadow-sm p-8">
          <div className="text-center">
            <CheckCircleIcon className="mx-auto h-12 w-12 text-green-500" />
            <h2 className="mt-4 text-xl font-bold text-gray-900 dark:text-gray-100">
              Sikeres regisztráció!
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Fiókod sikeresen létrejött. Átirányítás az ügyfélportálra...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative bg-[#f7f7f9] dark:bg-[#0b0d12] flex items-center justify-center overflow-hidden">
      {/* Theme and Language Selectors */}
      <div className="absolute top-4 right-4 z-20 flex items-center space-x-3">
        {/* Theme Selector */}
        <button
          onClick={toggleTheme}
          className="p-2 rounded-lg bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm hover:bg-white/90 dark:hover:bg-gray-800/90 transition-all duration-200 shadow-md"
          aria-label={theme === 'light' ? 'Váltás sötét módra' : 'Váltás világos módra'}
        >
          {theme === 'light' ? (
            <MoonIcon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
          ) : (
            <SunIcon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
          )}
        </button>

        {/* Language Selector */}
        <button
          onClick={() => setLanguage(language === 'hu' ? 'en' : 'hu')}
          className="p-2 rounded-lg bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm hover:bg-white/90 dark:hover:bg-gray-800/90 transition-all duration-200 shadow-md flex items-center space-x-1"
          aria-label={language === 'hu' ? 'Switch to English' : 'Váltás magyarra'}
        >
          <GlobeAltIcon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 uppercase">
            {language === 'hu' ? 'EN' : 'HU'}
          </span>
        </button>
      </div>

      {/* Professional minimal background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[#f7f7f9] dark:bg-[#0b0d12]">
          <div className="hidden" />
          <div className="hidden" />
          <div className="absolute inset-0 opacity-[0.015] dark:opacity-[0.03]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Ccircle cx='7' cy='7' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>
      </div>

      {/* Custom animations */}
      <style>{`
        @keyframes gradient-slow {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes gradient-slower {
          0%, 100% { background-position: 100% 50%; }
          50% { background-position: 0% 50%; }
        }
        @keyframes gradient-medium {
          0%, 100% { background-position: 50% 0%; }
          50% { background-position: 50% 100%; }
        }
        .animate-gradient-slow {
          background-size: 200% 200%;
          animation: gradient-slow 15s ease infinite;
        }
        .animate-gradient-slower {
          background-size: 200% 200%;
          animation: gradient-slower 20s ease infinite;
        }
        .animate-gradient-medium {
          background-size: 200% 200%;
          animation: gradient-medium 12s ease infinite;
        }
      `}</style>

      {/* Login container */}
      <div className="relative max-w-md w-full m-4">
        <div className="bg-white dark:bg-[#12151c] p-6 sm:p-8 rounded-2xl shadow-sm border border-gray-200 dark:border-[#2a2f3c]">
          {/* Logo */}
          <div className="text-center mb-8">
            <Logo className="h-12 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Ügyfél Regisztráció
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Hozd létre a jelszavadat a fiókodhoz
            </p>
          </div>

        {/* Customer Info */}
        <div className="mb-6 p-4 bg-gray-50 dark:bg-[#0b0d12] border border-gray-200 dark:border-[#2a2f3c] rounded-lg">
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
            Fiók adatok
          </h3>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            <strong>Név:</strong>{' '}
            {info?.customer.name}
          </p>
          <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
            <strong>Email:</strong> {info?.customer.email}
          </p>
          {info?.contactName && info.customer.companyName && (
            <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
              <strong>Cég:</strong> {info.customer.companyName}{info.organizationName ? ` · ${info.organizationName}` : ''}
            </p>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Password */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
              Jelszó *
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="w-full h-10 px-3 text-sm border rounded-lg bg-white dark:bg-[#0b0d12] border-gray-300 dark:border-[#2a2f3c] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#0f7f75]/40 focus:border-[#0f7f75] pr-10"
                placeholder="Minimum 6 karakter"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
              Jelszó megerősítése *
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
              className="w-full h-10 px-3 text-sm border rounded-lg bg-white dark:bg-[#0b0d12] border-gray-300 dark:border-[#2a2f3c] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#0f7f75]/40 focus:border-[#0f7f75]"
              placeholder="Jelszó újra"
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full h-11 inline-flex items-center justify-center rounded-lg bg-[#0f7f75] text-white text-sm font-medium hover:bg-[#0c6b62] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Regisztráció...
              </>
            ) : (
              <>
                <UserPlusIcon className="h-5 w-5 mr-2" />
                Fiók létrehozása
              </>
            )}
          </button>
        </form>

        {/* Login Link */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Már van fiókod?{' '}
            <button
              onClick={() => navigate('/login')}
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              Bejelentkezés
            </button>
          </p>
        </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerSignup;
